import * as THREE from 'three';
import { simToWorld } from '@somnhold/render';
import type { BattleState, Rift } from '@somnhold/sim';
import { h, setHidden, setStyle, viewH, viewW } from '../ui/dom.ts';

/** A chevron pointing down; turned to point at a place off screen (the crypt's arrows). */
const CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l7 8 7-8"/></svg>';

/** After a fissure opens, its arrow stays this many wave ticks (2 s). */
const AFTER = 24;

/** The general's fissures worth an arrow now: trembling, or just opened (a decoy looks the same until it closes). */
const cued = (s: BattleState): Rift[] => {
  const t = s.waveT;
  return (s.rifts?.list ?? []).filter((r) => t >= r.warn && t < r.open + AFTER && !(r.close > 0 && t >= r.close));
};

/**
 * Terraced land: a fissure the general cracks open mid-wave out of view gets a red arrow held at the screen's
 * edge pointing to it while the ground trembles and for a moment after (the camera sits on the keep, and the
 * fissures crack 8 hexes or more away). No words: it says where, never what, and a decoy gets one too.
 */
export class RiftCue {
  readonly el = h('div.dg-hud.pl-ways');
  private els: { el: HTMLElement; arrow: HTMLElement }[] = [];
  private v = new THREE.Vector3();

  constructor(private s: () => BattleState) {}

  place(camera: THREE.Camera): void {
    const rifts = cued(this.s());
    const W = viewW();
    const H = viewH();
    const v = this.v;
    rifts.forEach((r, k) => {
      let w = this.els[k];
      if (!w) {
        w = { el: h('div.dg-way.danger.edge', { 'aria-hidden': 'true' }), arrow: h('span.dg-way-arrow', { html: CHEVRON }) };
        w.el.append(w.arrow);
        this.el.append(w.el);
        this.els[k] = w;
      }
      v.copy(simToWorld(r.x, r.y)).setY(0.6).project(camera);
      const behind = v.z > 1;
      let px = (v.x * 0.5 + 0.5) * W;
      let py = (-v.y * 0.5 + 0.5) * H;
      if (behind) {
        px = W - px;
        py = H - py;
      }
      const m = 46;
      const top = W <= 640 ? 200 : 120;
      const inView = !behind && px > m && px < W - m && py > top && py < H - m;
      // In view the fissure speaks for itself.
      setHidden(w.el, inView);
      if (inView) return;
      const cx = W / 2;
      const cy = (top + H - m) / 2;
      const dx = px - cx;
      const dy = py - cy;
      const k2 = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), (H - m - top) / 2 / Math.max(1, Math.abs(dy)));
      setStyle(w.el, 'transform', `translate(${(cx + dx * k2).toFixed(1)}px, ${(cy + dy * k2).toFixed(1)}px) translate(-50%, -50%)`);
      setStyle(w.arrow, 'transform', `rotate(${(Math.atan2(dy, dx) - Math.PI / 2).toFixed(3)}rad)`);
    });
    for (let k = rifts.length; k < this.els.length; k++) setHidden((this.els[k] as { el: HTMLElement }).el, true);
  }
}
